<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, reactive, ref, watch } from "vue"
import { Subscription } from "rxjs"
import { useUploads, UploadPhase, type Upload, type UploadState } from "./Uploads"
import { useUploadDialogState } from "./uploadDialogState"
import { createUploadEditSession } from "./useUploadEditSession"
import { UploadEditSessionKey } from "./UploadEditSessionKey"
import UploadMasterList from "./UploadMasterList.vue"
import UploadDetailPane from "./UploadDetailPane.vue"
import { XMarkIcon } from "@heroicons/vue/24/outline"
import { useApolloClient } from "@vue/apollo-composable"
import {
  GetMediaDocument,
  useUpdateMediaMutation,
  type GetMediaQuery,
  type GetMediaQueryVariables,
  type MediaDetailsFragment,
  type UpdateMediaMutationVariables,
} from "../graphql/types"
import { createEmptyMediaFormData, toMediaFormData, toUpdateInput, type MediaFormData } from "../media/media"
import { notifyError, notifySuccess } from "../notifications"

interface DialogRow {
  // Stable per-row key, assigned at creation. `id` (the media uuid) only arrives
  // once the upload completes, so it cannot key the row while it is still running.
  localId: string
  id: string
  upload: Upload
  state: UploadState
  preview?: string
  previewUrl?: string
  filename: string
  stateSubscription: { unsubscribe(): void }
}

const uploads = useUploads()
const dialog = useUploadDialogState()

const rows = ref<DialogRow[]>([])
let rowSeq = 0
let uploadsSubscription: Subscription | undefined

// Full-fragment server state per saved media, used to rebuild drafts after
// save. The upload stream only emits MediaListItemFragment which lacks
// copyright/license/source-url/tags/games/consoles — those fields would
// otherwise silently reset when a draft is discarded and later rebuilt.
const savedMediaById = ref<Map<string, MediaDetailsFragment>>(new Map())

const { mutate: updateMedia } = useUpdateMediaMutation({})
const { client: apolloClient } = useApolloClient()

async function persist(id: string, input: UpdateMediaMutationVariables): Promise<void> {
  const result = await updateMedia(input)
  if (result?.errors?.length) {
    notifyError({ description: result.errors.map(e => e.message).join(", ") })
    throw new Error(result.errors[0]?.message ?? "GraphQL error")
  }
  if (result?.data?.updateMedia) {
    const saved = result.data.updateMedia
    const next = new Map(savedMediaById.value)
    next.set(id, saved)
    savedMediaById.value = next
    notifySuccess({ text: `Datei ${saved.filename} gespeichert` })
  }
}

const session = createUploadEditSession({
  saveDraft: async (id, draft) => { await persist(id, toUpdateInput(draft)) },
  saveUpdate: async (id, update) => { await persist(id, { id, update }) }
})

provide(UploadEditSessionKey, session)

function deriveBasename(filename: string): string {
  const i = filename.lastIndexOf(".")
  return i >= 0 ? filename.substring(0, i) : filename
}

function buildBaselineForRow(row: DialogRow): MediaFormData {
  // Prefer the full-fragment snapshot from the last successful save over the
  // upload stream's reduced list-fragment — the stream never delivers
  // copyright/license/source-url/tags/games/consoles for new uploads.
  const saved = savedMediaById.value.get(row.id)
  if (saved) return toMediaFormData(saved)

  const media = row.state.media
  const base: MediaFormData = {
    ...createEmptyMediaFormData(),
    uuid: row.id,
    filename: row.filename,
    basename: deriveBasename(row.filename)
  }
  if (!media) return base

  return {
    ...base,
    uuid: media.uuid,
    filename: media.filename,
    basename: deriveBasename(media.filename),
    // MediaListItemFragment carries reduced tag/game/console shapes that don't
    // satisfy the full *ListItemFragment types required by MediaFormData. Use
    // empty arrays here; full data is read from savedMediaById after first save.
    tags: [],
    companies: media.companies ?? [],
    gameSeries: media.gameSeries ?? [],
    games: [],
    gameConsoles: [],
    watermark: media.watermark ?? false
  }
}

function ensureDraftForRow(row: DialogRow): MediaFormData {
  return session.ensureDraft(row.id, buildBaselineForRow(row))
}

function isDraftDirty(id: string): boolean {
  const draft = session.formDrafts.value.get(id)
  if (!draft) return false
  const row = rows.value.find(r => r.id === id)
  if (!row) return false
  const baseline = buildBaselineForRow(row)
  return JSON.stringify(draft) !== JSON.stringify(baseline)
}

const fetchingMediaIds = new Set<string>()

// The upload stream only delivers a reduced MediaListItemFragment, whose
// tags/games/gameConsoles shapes don't satisfy MediaFormData — so a fresh
// upload's auto-linked games/consoles would be invisible in the form and
// wiped on save. Fetch the full MediaDetails once the upload completes and
// store it as the baseline source; rebuild the draft if it is still pristine.
async function loadFullMedia(id: string): Promise<void> {
  if (fetchingMediaIds.has(id) || savedMediaById.value.has(id)) return
  fetchingMediaIds.add(id)
  try {
    const { data } = await apolloClient.query<GetMediaQuery, GetMediaQueryVariables>({
      query: GetMediaDocument,
      variables: { id },
      fetchPolicy: "no-cache",
    })
    const full = data?.media
    if (!full) return
    const wasDirty = isDraftDirty(id)
    const next = new Map(savedMediaById.value)
    next.set(id, full)
    savedMediaById.value = next
    if (!wasDirty && session.formDrafts.value.has(id)) {
      session.discardDraft(id)
      const row = rows.value.find(r => r.id === id)
      if (row) ensureDraftForRow(row)
    }
  } finally {
    fetchingMediaIds.delete(id)
  }
}

onMounted(() => {
  window.addEventListener("keydown", onKeydown)
  uploadsSubscription = uploads.subscribe((upload) => {
    const row = reactive<DialogRow>({
      localId: `row-${++rowSeq}`,
      id: "",
      upload,
      state: { phase: UploadPhase.CREATING },
      preview: undefined,
      previewUrl: undefined,
      filename: upload.name,
      stateSubscription: Subscription.EMPTY
    })

    if (upload.file) {
      const reader = new FileReader()
      reader.onloadend = () => { row.preview = reader.result as string }
      reader.readAsDataURL(upload.file)
    }

    row.stateSubscription = upload.state.subscribe((state) => {
      row.state = state
      if (state.media) {
        row.id = state.media.uuid
        row.filename = state.media.filename
        row.previewUrl = state.media.previewUrl128 ?? row.previewUrl
        void loadFullMedia(state.media.uuid)
      }
    })

    rows.value = [row, ...rows.value]
  })
})

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown)
  uploadsSubscription?.unsubscribe()
  rows.value.forEach(r => r.stateSubscription.unsubscribe())
})

watch(() => dialog.isOpen.value, (open) => {
  document.body.style.overflow = open ? "hidden" : ""
})
onUnmounted(() => { document.body.style.overflow = "" })

const items = computed(() =>
  rows.value.map(r => ({
    // While the upload runs, `r.id` is "" — keep the row visible (keyed by
    // localId) so its progress shows, but it is not yet selectable/editable.
    key: r.localId,
    id: r.id,
    ready: r.id !== "",
    filename: r.filename,
    preview: r.previewUrl ?? r.preview,
    phase: r.state.phase,
    progress: r.state.progress,
    selected: r.id !== "" && session.selectedIds.value.has(r.id),
    active: r.id !== "" && session.activeId.value === r.id,
    dirty: r.id !== "" && isDraftDirty(r.id),
    error: r.state.error
  }))
)

const candidates = computed(() =>
  items.value
    .filter(i => i.ready)
    .map(i => ({
      id: i.id,
      filename: i.filename,
      draft: session.formDrafts.value.get(i.id) ?? createEmptyMediaFormData()
    }))
)

function cycleActive(delta: number): void {
  const list = items.value.filter(i => i.ready)
  if (list.length === 0) return
  const currentIdx = list.findIndex(i => i.id === session.activeId.value)
  const nextIdx = currentIdx < 0 ? 0 : (currentIdx + delta + list.length) % list.length
  const next = list[nextIdx]
  if (!next) return
  session.activeId.value = next.id
  session.selectedIds.value = new Set([next.id])
}

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable
}

function onKeydown(event: KeyboardEvent): void {
  if (!dialog.isOpen.value) return
  // List-navigation/selection shortcuts must not hijack keystrokes typed into
  // an edit field — otherwise Space toggles the item off (closing the form) and
  // arrows move the cursor selection instead of caret. Cmd/Ctrl+S (save) and
  // Escape stay active while typing.
  const typing = isEditableTarget(event.target)
  if (event.key === "ArrowDown" && !typing) { event.preventDefault(); cycleActive(1) }
  else if (event.key === "ArrowUp" && !typing) { event.preventDefault(); cycleActive(-1) }
  else if (event.key === " " && !typing && session.activeId.value) {
    event.preventDefault()
    onToggleSelect(session.activeId.value)
  }
  else if ((event.metaKey || event.ctrlKey) && event.key === "s") {
    event.preventDefault()
    if (session.mode.value === "single" && session.activeId.value) {
      session.saveSingle(session.activeId.value).catch(err => {
        notifyError({ description: `Datei konnte nicht gespeichert werden: ${err instanceof Error ? err.message : String(err)}` })
      })
    }
  }
  else if (event.key === "Escape") {
    if (session.selectedIds.value.size > 1) {
      session.selectedIds.value = new Set()
      session.activeId.value = null
    }
  }
}

const previewUrls = computed<Record<string, string | undefined>>(() => {
  const map: Record<string, string | undefined> = {}
  for (const r of rows.value) if (r.id) map[r.id] = r.previewUrl ?? r.preview
  return map
})

function onActivate(id: string): void {
  const row = rows.value.find(r => r.id === id)
  if (!row) return
  ensureDraftForRow(row)
  session.activeId.value = id
  if (!session.selectedIds.value.has(id)) {
    session.selectedIds.value = new Set([id])
  }
}

function onToggleSelect(id: string): void {
  const row = rows.value.find(r => r.id === id)
  if (!row) return
  ensureDraftForRow(row)

  const next = new Set(session.selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  session.selectedIds.value = next

  if (session.activeId.value === id && !next.has(id)) {
    session.activeId.value = next.size > 0 ? (Array.from(next)[0] ?? null) : null
  } else if (next.size === 1) {
    session.activeId.value = Array.from(next)[0] ?? null
  }
}

function onToggleAll(selected: boolean): void {
  if (selected) {
    for (const row of rows.value) if (row.id) ensureDraftForRow(row)
    session.selectedIds.value = new Set(items.value.filter(i => i.ready).map(i => i.id))
  } else {
    session.selectedIds.value = new Set()
    session.activeId.value = null
  }
}

function onFilesDropped(files: FileList): void {
  for (const file of Array.from(files)) uploads.upload(file)
}

function onUrlsImported(urls: URL[]): void {
  for (const url of urls) uploads.import(url)
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="dialog.isOpen.value"
      data-testid="upload-dialog"
      class="fixed inset-0 z-50 bg-gray-900/30 backdrop-blur-sm flex"
    >
      <div class="m-4 lg:m-12 flex-1 min-w-0 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
        <div class="flex items-center justify-between px-4 py-3 border-b">
          <div class="font-semibold">Uploads ({{ items.length }})</div>
          <button class="btn btn-sm btn-ghost" @click="dialog.close()">
            <XMarkIcon class="w-5 h-5"/>
          </button>
        </div>
        <div class="flex-1 flex min-w-0 overflow-hidden">
          <div class="w-full lg:w-96 border-r flex flex-col min-w-0 overflow-hidden">
            <UploadMasterList
              :items="items"
              @toggle-select="onToggleSelect"
              @toggle-all="onToggleAll"
              @activate="onActivate"
              @files-dropped="onFilesDropped"
              @urls-imported="onUrlsImported"
            />
          </div>
          <div data-testid="pane-desktop" class="hidden lg:flex flex-1 min-w-0 overflow-hidden">
            <UploadDetailPane :candidates="candidates" :preview-urls="previewUrls"/>
          </div>

          <div
            v-if="session.mode.value !== 'empty'"
            data-testid="pane-mobile"
            class="lg:hidden fixed inset-0 z-[60] bg-white flex flex-col"
          >
            <div class="p-3 border-b flex items-center gap-2">
              <button
                class="btn btn-sm btn-ghost"
                @click="session.selectedIds.value = new Set(); session.activeId.value = null"
              >
                ← Zurück
              </button>
            </div>
            <div class="flex-1 overflow-hidden">
              <UploadDetailPane :candidates="candidates" :preview-urls="previewUrls"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
